/*
 * Copyright (c) Microsoft. All rights reserved. Licensed under the MIT license. See full license in root of repo. -->
 *
 * This file shows how to use MSAL.js to get an access token to your server and pass it to the task pane.
 */

/* global console, localStorage, location, Office, window, process */

import { Configuration, LogLevel, PublicClientApplication, RedirectRequest } from "@azure/msal-browser";
import { getExtensions } from "../api/MiddleTier";

const clientId = process.env.REACT_APP_CLIENT_ID;
const clientScope = process.env.REACT_APP_CLIENT_SCOPE;
const accessScope = `${clientScope}/access_as_user`;
const loginRequest: RedirectRequest = {
  scopes: [accessScope],
  extraScopesToConsent: ["user.read", "mail.read", `${process.env.REACT_APP_GOS_CLIENT_ID}/access_as_user`],
};

const msalConfig: Configuration = {
  auth: {
    clientId: clientId,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_TENANT_ID}`,
    redirectUri: `https://${window.location.host}/fallbackauthdialog.html`,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage", // Needed to avoid "User login is required" error.
    storeAuthStateInCookie: true, // Recommended to avoid certain IE/Edge issues.
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

const publicClientApp: PublicClientApplication = new PublicClientApplication(msalConfig);

let loginDialog: Office.Dialog = null;
let homeAccountId = null;
// Only one Office dialog can be open at a time, so concurrent callers share the same pending sign-in.
let pendingDialogToken: Promise<string> = null;
let resolveDialogToken: (accessToken: string) => void = null;
let rejectDialogToken: (error: Error) => void = null;

Office.onReady(async () => {
  await publicClientApp.initialize();
  if (Office.context.ui.messageParent) {
    publicClientApp
      .handleRedirectPromise()
      .then(handleResponse)
      .catch((error) => {
        console.log(error);
        Office.context.ui.messageParent(JSON.stringify({ status: "failure", result: error }));
      });

    // The very first time the add-in runs on a developer's computer, msal.js hasn't yet
    // stored login data in localStorage. So a direct call of acquireTokenRedirect
    // causes the error "User login is required". Once the user is logged in successfully
    // the first time, msal data in localStorage will prevent this error from ever hap-
    // pening again; but the error must be blocked here, so that the user can login
    // successfully the first time. To do that, call loginRedirect first instead of
    // acquireTokenRedirect.
    if (localStorage.getItem("loggedIn") === "yes") {
      publicClientApp.acquireTokenRedirect(loginRequest);
    } else {
      // This will login the user and then the (response.tokenType === "id_token")
      // path in authCallback below will run, which sets localStorage.loggedIn to "yes"
      // and then the dialog is redirected back to this script, so the
      // acquireTokenRedirect above runs.
      publicClientApp.loginRedirect(loginRequest);
    }
  }
});

function handleResponse(response) {
  if (response.tokenType === "id_token") {
    console.log("LoggedIn");
    localStorage.setItem("loggedIn", "yes");
  } else {
    console.log("token type is:" + response.tokenType);
    Office.context.ui.messageParent(
      JSON.stringify({ status: "success", result: response.accessToken, accountId: response.account.homeAccountId })
    );
  }
}

/**
 * Gets a middle tier access token without Office SSO.
 * Uses the MSAL cache if the user already signed in through the dialog, otherwise opens the
 * login dialog. When `interactive` is false, the dialog is never opened and the call fails instead.
 */
export async function getAccessTokenViaDialog(interactive: boolean = true): Promise<string> {
  // Attempt to acquire token silently if user is already signed in.
  if (homeAccountId !== null) {
    try {
      const result = await publicClientApp.acquireTokenSilent(loginRequest);
      if (result?.accessToken) {
        return result.accessToken;
      }
    } catch (error) {
      console.warn("Silent token acquisition failed.", error);
    }
  }

  if (!interactive) {
    throw new Error("Interactive sign-in is required.");
  }

  if (!pendingDialogToken) {
    pendingDialogToken = new Promise<string>((resolve, reject) => {
      resolveDialogToken = resolve;
      rejectDialogToken = reject;
      // We fall back to Dialog API for any error.
      showLoginPopup("/fallbackauthdialog.html");
    });
    const clearPending = () => {
      pendingDialogToken = null;
    };
    pendingDialogToken.then(clearPending, clearPending);
  }
  return pendingDialogToken;
}

export async function dialogFallback(callback) {
  let accessToken: string;
  try {
    accessToken = await getAccessTokenViaDialog();
  } catch (error) {
    console.error(error);
    callback();
    return;
  }

  try {
    const response = await getExtensions(accessToken, {
      messageId: Office.context.mailbox.convertToRestId(
        Office.context.mailbox.item.itemId,
        Office.MailboxEnums.RestVersion.v2_0
      ),
    });
    callback(response);
  } catch (error) {
    // If there are no extensions, the API will return a 404 error.
    console.error(error);
    callback();
  }
}

// This handler responds to the success or failure message that the pop-up dialog receives from the identity provider
// and access token provider.
async function processMessage(arg) {
  // Uncomment to view message content in debugger, but don't deploy this way since it will expose the token.
  //console.log("Message received in processMessage: " + JSON.stringify(arg));

  let messageFromDialog = JSON.parse(arg.message);

  if (messageFromDialog.status === "success") {
    // We now have a valid access token.
    loginDialog.close();

    // Configure MSAL to use the signed-in account as the active account for future requests.
    const homeAccount = publicClientApp.getAccount({ homeAccountId: messageFromDialog.accountId });
    if (homeAccount) {
      homeAccountId = messageFromDialog.accountId; // Track the account id for future silent token requests.
      publicClientApp.setActiveAccount(homeAccount);
    }

    resolveDialogToken?.(messageFromDialog.result);
  } else if (messageFromDialog.error === undefined && messageFromDialog.result.errorCode === undefined) {
    // Need to pick the user to use to auth
  } else {
    // Something went wrong with authentication or the authorization of the web application.
    loginDialog.close();
    let errorMessage = "Fallback authentication failed.";
    if (messageFromDialog.error) {
      errorMessage = messageFromDialog.error.toString();
    } else if (messageFromDialog.result) {
      errorMessage = messageFromDialog.result.errorMessage?.toString() ?? errorMessage;
    }
    console.error(JSON.stringify(errorMessage));
    rejectDialogToken?.(new Error(errorMessage));
  }
}

// Fires when the user closes the dialog or it fails to load (e.g. 12006 = closed by the user).
function processDialogEvent(arg: any) {
  rejectDialogToken?.(new Error(`Login dialog closed (${arg?.error})`));
}

// Use the Office dialog API to open a pop-up and display the sign-in page for the identity provider.
function showLoginPopup(url) {
  var fullUrl = location.protocol + "//" + location.hostname + (location.port ? ":" + location.port : "") + url;

  // height and width are percentages of the size of the parent Office application, e.g., PowerPoint, Excel, Word, etc.
  Office.context.ui.displayDialogAsync(fullUrl, { height: 60, width: 30 }, function (result) {
    if (result.status !== Office.AsyncResultStatus.Succeeded) {
      console.error(`Failed to open login dialog: ${result.error.code} ${result.error.message}`);
      rejectDialogToken?.(new Error(`Failed to open login dialog (${result.error.code})`));
      return;
    }
    console.log("Dialog has initialized. Wiring up events");
    loginDialog = result.value;
    loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processMessage);
    loginDialog.addEventHandler(Office.EventType.DialogEventReceived, processDialogEvent);
  });
}
